import type {
  PhrasingContent,
  Root,
  Text,
  ListItem,
  List,
  Paragraph,
} from 'mdast';
import type { Plugin } from 'unified';
import { u } from 'unist-builder';
import flatMap from 'unist-util-flatmap';
import { type BuildVisitor, visit } from 'unist-util-visit';

declare module 'mdast' {
  interface ListItemData {
    prefix?: string;
  }
  interface ParagraphData {
    prefix?: string;
  }
}

type RootChild = Root['children'][number];

type FlattenedList = Omit<List, 'children'> & {
  children: Paragraph[];
};

type FlattenedRootChild = Exclude<RootChild, List | ListItem>;

interface FlatteningRoot {
  type: 'root';
  children: (RootChild | FlattenedList)[];
}

interface FlattenedRoot {
  type: 'root';
  children: FlattenedRootChild[];
}

const filterText = (node: PhrasingContent): node is Text => {
  return node.type === 'text';
};

/**
 * Removes `list` and `listItem` nodes and adds `prefix` properties to
 * the data for the `listItem` children. For ordered lists, prefixes are
 * `1. `, `2. `, ... unordered lists are `- `, `- `, ...
 */
export const flattenLists: Plugin<[], Root, FlattenedRoot> = function () {
  return function (tree) {
    return flatMap(
      tree,
      (node: Parameters<BuildVisitor<FlatteningRoot>>['0']) => {
        if (node.type === 'list') {
          // At this point the listItem nodes have already been
          // flattened and the children are those of listItem.
          // nested lists were already flattened in a previous pass
          // and have prefix defined.
          return node.children.map((child, idx) => {
            child.data = {
              prefix:
                child.data?.prefix ??
                (node.ordered ? `${idx + (node.start ?? 1)}. ` : '- '),
            };

            return child;
          });
        }

        if (node.type === 'listItem') {
          return node.children;
        }

        return [node];
      }
    );
  };
};

export const condense: Plugin<[], FlattenedRoot, FlattenedRoot> = function () {
  return function (tree) {
    visit<FlattenedRoot, 'paragraph'>(
      tree,
      'paragraph',
      (node, index, parent) => {
        if (!parent || typeof index !== 'number') {
          return;
        }

        if (node.data && 'prefix' in node.data && node.data?.prefix) {
          node.children.unshift({ type: 'text', value: node.data.prefix });
        }
      }
    );

    visit<FlattenedRoot, 'link'>(tree, 'link', (node, index, parent) => {
      if (!parent || typeof index !== 'number') {
        return;
      }

      const text = `[${node.children
        .filter(filterText)
        .map((child) => child.value)
        .join(' ')}]`;

      parent.children[index] = { type: 'text', value: text };
    });

    visit<FlattenedRoot, 'heading'>(tree, 'heading', (node, index, parent) => {
      if (!parent || typeof index !== 'number') {
        return;
      }

      parent.children[index] = u('paragraph', [u('strong', node.children)]);
    });

    visit<FlattenedRoot, 'code'>(tree, 'code', (node, index, parent) => {
      if (!parent || typeof index !== 'number') {
        return;
      }

      node.value = `\`\`\`\n${node.value}\n\`\`\``;
    });

    visit<FlattenedRoot, 'inlineCode'>(
      tree,
      'inlineCode',
      (node, index, parent) => {
        if (!parent || typeof index !== 'number') {
          return;
        }

        parent.children[index] = {
          type: 'text',
          value: `\`${node.value}\``,
        };
      }
    );
  };
};

const remarkCondense = [flattenLists, condense];

export default remarkCondense;
